// Data only (PostHog client spec §6): event names, property allow-lists, enums and
// the exception schema. analytics-scrub.js enforces these; call sites use EVENTS and
// never invent a name. Imports only the three leaf data modules (spec §3 dependency rule).
import { SPORTSBOOKS, SUPPORTED_LEAGUES } from './constants';
import { PAGE } from '../constants/pages';
import {
  OUTCOMES as LEDGER_OUTCOMES, STAGES as LEDGER_STAGES, REASON_CODES as LEDGER_REASONS, BOOK_ERROR_CLASSES,
  BOOK_ANSWER_REASONS, CONTENT_TYPE_CLASSES, TRAIL_TOKENS, STOPPED_AT,
} from './slipsync/attempt-exit-map';

export const EVENTS = Object.freeze({
  SCREEN_VIEWED: 'screen_viewed',
  SIGN_IN_COMPLETED: 'sign_in_completed',
  IMPORT_STARTED: 'import_started',
  IMPORT_COMPLETED: 'import_completed',
  IMPORT_FAILED: 'import_failed',
  SLIP_REMOVED: 'slip_removed',
  SLIPSYNC_RUN_COMPLETED: 'slipsync_run_completed',
  SLIPSYNC_REFUSED: 'slipsync_refused',
  BUG_REPORT_SUBMITTED: 'bug_report_submitted',
  CLIENT_WARNING: 'client_warning',
  ONBOARDING_STEP_VIEWED: 'onboarding_step_viewed',
});

export const SDK_EVENTS = Object.freeze(['$exception', '$snapshot', '$identify']);

// Closed: anything not listed is dropped by scrub().
export const ALLOWED_EVENTS = new Set([...Object.values(EVENTS), ...SDK_EVENTS]);

export const COMMON_PROPS = Object.freeze([
  'token', 'distinct_id', '$device_id', '$session_id', '$window_id', '$insert_id', '$time', '$lib',
  '$lib_version', '$is_identified', '$process_person_profile', '$os', '$os_version', '$device_type',
  '$screen_height', '$screen_width', '$viewport_height', '$viewport_width', '$timezone',
  'app_version', 'platform',
]);

export const EVENT_PROPS = Object.freeze({
  screen_viewed: ['screen'],
  sign_in_completed: [],
  import_started: [],
  slip_removed: [],
  bug_report_submitted: [],
  import_completed: ['book', 'league', 'leg_count'],
  import_failed: ['stage', 'reason_code', 'detected_sport'], // detected_sport: #1652 (sport_mismatch only)
  slipsync_run_completed: [
    'book', 'outcome', 'attempt_id',
    // #1587: why the attempt ended. Codes and bounded counts only (analytics-scrub).
    'stage', 'exit_reason', 'http_status', 'bridge_last', 'last_auth_reason', 'last_skip',
    'injects', 'auth_errors', 'unbound', 'slip_count',
    // 2026-10-18 visibility (#1568): the book's coded answer and the bridge trail.
    'error_class', 'book_reason', 'content_type', 'keys', 'trail', 'stopped_at',
    // #1711: DraftKings' JSON-RPC error code (a bounded int32, analytics-scrub).
    'rpc_code',
    // #1659: the server's result counts on a complete exit (bounded counts, analytics-scrub).
    'imported_count', 'skipped_count', 'failed_count', 'untracked_count',
  ],
  slipsync_refused: ['book', 'reason'],
  client_warning: ['fn', 'stage', 'boundary'],
  onboarding_step_viewed: ['step'],
  $exception: ['$exception_list', '$exception_level', '$release_id', 'fn', 'stage', 'boundary'],
  $snapshot: ['$snapshot_data', '$snapshot_bytes', '$snapshot_host'],
  $identify: ['$anon_distinct_id'],
});

export const SCREENS = Object.freeze(['home', 'bets', 'research', 'history', 'bookies']);
// Onboarding steps that may be tracked: the gate's ANALYTICS_STEPS (spec D9; PR 3 adds orientation).
export const ONBOARDING_STEPS = Object.freeze(['signin', 'app']);
export const BOOKS = Object.freeze(SPORTSBOOKS.map((b) => b.id));
export const LEAGUES = Object.freeze([...SUPPORTED_LEAGUES]);
export const OUTCOMES = Object.freeze([...LEDGER_OUTCOMES]);
// #1587: the SlipSync exit event's coded fields. AUTH_REASONS is every reason parseBetslipsMessage accepts
// (attempt-exit-map BOOK_ANSWER_REASONS, betmgm-client.js) plus 'unknown'; BRIDGE_STEPS is every token the bridge
// writes to its trail (notes, not_signed_in:<reason>, route:<name>), since bridge_last is the trail's last token.
export const SYNC_STAGES = Object.freeze([...LEDGER_STAGES]);
export const SYNC_EXIT_REASONS = Object.freeze([...LEDGER_REASONS]);
export const AUTH_REASONS = Object.freeze([...BOOK_ANSWER_REASONS, 'unknown']);
export const SKIP_REASONS = Object.freeze(['signin_page', 'off_host', 'ceiling', 'same_url']);
export const BRIDGE_STEPS = Object.freeze([...TRAIL_TOKENS]);
// #1568: a failed run's book error class, the book's last not-signed-in answer and the bridge trail.
export const SYNC_ERROR_CLASSES = Object.freeze([...BOOK_ERROR_CLASSES]);
export const SYNC_CONTENT_TYPES = Object.freeze([...CONTENT_TYPE_CLASSES]);
export const SYNC_STOPPED_AT = Object.freeze([...STOPPED_AT]);
export const SYNC_TRAIL_MAX = 40; // tokens; the bridge's MAX_TRAIL
export const SYNC_KEY_RE = /^[A-Za-z][A-Za-z0-9_]{0,39}$/; // same rule as betmgm-client.js: a letter first
export const SYNC_KEYS_MAX = 10;
// Bridge hardening (spec §6): why a SlipSync tap opened nothing.
export const REFUSAL_REASONS = Object.freeze(['paused', 'in_flight', 'too_soon', 'cooldown', 'reconnect_required', 'take_a_break', 'unsupported_book', 'access_denied']); // access_denied: #1509 (triggerSync access gate)
export const IMPORT_STAGES = Object.freeze(['pick', 'vision', 'save']);
export const IMPORT_REASONS = Object.freeze([
  'image_unreadable', 'network_error', 'http_error', 'timeout', 'parse_failed', 'server_refused', 'daily_cap', 'unknown',
  'sport_mismatch', // #1652: a screenshot from a league other than the chosen one
]);
// #1652: the league a refused screenshot's legs were labelled with (api/lib/vision-sport-check.js).
export const DETECTED_SPORTS = Object.freeze(['nhl', 'mlb', 'nfl', 'nba', 'other']);

// Coded tags (fn / stage / boundary) on client_warning and $exception.
export const TAG_RE = /^[A-Za-z0-9_.:-]{1,64}$/;
export const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// App.js `page` index -> screen name (src/constants/pages.js inverted, keys lower-cased).
export const PAGE_NAME = Object.freeze(
  Object.fromEntries(Object.entries(PAGE).map(([key, index]) => [index, key.toLowerCase()])),
);

export const EXCEPTION = Object.freeze({
  MAX_ENTRIES: 5,
  MAX_FRAMES: 50,
  TYPE_RE: /^[A-Za-z0-9_.$]{1,64}$/,
  FUNCTION_RE: /^[A-Za-z0-9_.$<>?]{1,128}$/,
  SOURCE_RE: /^[A-Za-z0-9_.$[\]]+$/,
  RELEASE_RE: /^[A-Za-z0-9_-]{1,64}$/,
  LEVELS: Object.freeze(['fatal', 'error', 'warning', 'info', 'debug']),
});
